import React from 'react'
import { Button, Form, Input, Modal } from 'antd'
import type { InputRef } from 'antd/lib/input'
import { useTranslation } from '@nic/commons'
import { useAuth } from '@nic/kc-auth'
import { NicScope } from '@nic/nic-api/types'
import { getProxiedRequest, getRequest } from '@nic/nic-api'
import { useHistory } from 'react-router'

type RequestLookupVariant = 'desktop' | 'mobile'

interface RequestLookupModalProps {
  variant?: RequestLookupVariant
  buttonColor?: string
  className?: string
}
 
export const RequestLookupModal: React.FC<RequestLookupModalProps> = ({
  variant = 'desktop',
  buttonColor,
  className,
}) => {
  const { keycloak } = useAuth()
  const history = useHistory()
  const t = useTranslation()
  const realm = (keycloak?.realm as NicScope) || 'registrar'
  const [form] = Form.useForm()
  const inputRef = React.useRef<InputRef>(null)

  const [visible, setVisible] = React.useState(false)
  const [requestId, setRequestId] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const isRegistrar = realm === 'registrar'

  React.useEffect(() => {
    if (visible) {
      const timer = window.setTimeout(() => {
        inputRef.current?.focus()
      }, 0)

      return () => window.clearTimeout(timer)
    }
  }, [visible])

  const resetState = () => {
    setRequestId('')
    setError(null)
    setLoading(false)
    form.resetFields()
  }

  const closeModal = () => {
    setVisible(false)
    setLoading(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (/^\d*$/.test(value)) {
      setRequestId(value)
      setError(null)
    }
  }

  const fetchRequest = async (id: number) => {
    if (isRegistrar) {
      await getProxiedRequest(id)
    } else {
      await getRequest(id)
    }
  }

  const handleSubmit = async () => {
    if (!requestId) {
      setError(t('requestLookupModal.validation.required'))
      return
    }

    const numericId = Number(requestId)
    if (!Number.isFinite(numericId)) {
      setError(t('requestLookupModal.validation.required'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      await fetchRequest(numericId)
      closeModal()
      history.push(`/${realm}/requests/${numericId}`)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) {
        setError(t('requestLookupModal.validation.notFound', { id: requestId }))
      } else {
        setError(t('requestLookupModal.validation.generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  const buttonProps =
    variant === 'desktop'
      ? {
          type: 'primary' as const,
          ghost: true,
          style: {
            color: '#ffffff',
            borderColor: '#ffffff',
          },
        }
      : {
          type: 'primary' as const,
          block: true,
          style: buttonColor
            ? {
                backgroundColor: buttonColor,
                borderColor: buttonColor,
              }
            : undefined,
        }

  return (
    <>
      <Button
        {...buttonProps}
        className={className}
        onClick={() => setVisible(true)}
        data-testid='request-lookup-trigger'
      >
        {t('requestLookupModal.trigger')}
      </Button>
      <Modal
        visible={visible}
        title={t('requestLookupModal.title')}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('requestLookupModal.submit')}
        cancelText={t('requestLookupModal.cancel')}
        confirmLoading={loading}
        maskClosable={true}
        destroyOnClose={false}
        afterClose={resetState}
      >
        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          <Form.Item
            label={t('requestLookupModal.label')}
            validateStatus={error ? 'error' : undefined}
            help={error ?? t('requestLookupModal.description')}
          >
            <Input
              ref={inputRef}
              value={requestId}
              onChange={handleChange}
              inputMode='numeric'
              pattern='[0-9]*'
              placeholder={t('requestLookupModal.placeholder')}
              autoComplete='off'
            />
          </Form.Item>
          <Button htmlType='submit' style={{ display: 'none' }}>
            {t('requestLookupModal.submit')}
          </Button>
        </Form>
      </Modal>
    </>
  )
}
